import { useState } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@t-one-internal/design-system";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@t-one-internal/design-system";
import { resendWelcomeEmail } from "../api/account";

interface VerifyEmailState {
  email: string;
  first_name: string;
  user_uuid: string;
  account_uuid: string;
}

export default function VerifyEmail() {
  const location = useLocation();
  const state = location.state as VerifyEmailState | null;

  const [isResending, setIsResending] = useState(false);
  const [resendStatus, setResendStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const handleResendEmail = async () => {
    if (!state) {
      setResendStatus("error");
      setErrorMessage("Missing registration data. Please register again.");
      return;
    }

    setIsResending(true);
    setResendStatus("idle");
    setErrorMessage("");

    try {
      await resendWelcomeEmail({
        email: state.email,
        user_uuid: state.user_uuid,
        account_uuid: state.account_uuid,
        first_name: state.first_name,
      });
      setResendStatus("success");
    } catch (error: any) {
      setResendStatus("error");
      setErrorMessage(
        error.response?.data?.message ||
        error.message ||
        "Failed to resend email. Please try again."
      );
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <Card className="w-full max-w-md shadow-xl border-0">
        <CardHeader className="space-y-1 pb-8">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-green-600 rounded-xl flex items-center justify-center">
              <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-center text-gray-900">Check Your Email</CardTitle>
          <CardDescription className="text-center text-gray-600">
            We've sent a verification link to your email address
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md text-sm">
            <p className="font-medium">Organization created successfully!</p>
            <p className="mt-1">Please check your email and click the verification link to activate your account.</p>
          </div>

          {resendStatus === "success" && (
            <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-md text-sm">
              Verification email has been resent successfully!
            </div>
          )}

          {resendStatus === "error" && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
              {errorMessage}
            </div>
          )}

          <div className="text-center space-y-4">
            <p className="text-sm text-gray-600">
              Didn't receive the email? Check your spam folder or
            </p>
            <Button
              variant="outline"
              className="w-full h-11 border-gray-300 hover:bg-gray-50"
              onClick={handleResendEmail}
              disabled={isResending || !state}
            >
              {isResending ? "Resending..." : "Resend verification email"}
            </Button>
          </div>

          <div className="text-center text-sm text-gray-600">
            Need help?{" "}
            <button
              onClick={() => window.location.href = '/login'}
              className="text-blue-600 hover:underline font-medium"
            >
              Back to login
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}